import {
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HackathonService } from 'src/app/hackathon/hackathon.service';
import { LoginService } from 'src/app/login/login.service';
import { ClassesService } from 'src/app/shared/classes.service';
import { SelectedStudentService } from 'src/app/shared/selected-student.service';
import { ClassPickerService } from 'src/app/shared/class-picker-dialog/class-picker.service';
import { OrganizationService } from 'src/app/shared/organization.service';
import { WhatsappService } from 'src/app/whatsapp/whatsapp.service';
import { CompilerColorBloc } from 'src/app/translator/compiler-color.bloc';
import { environment } from 'src/environments/environment';
import { routeMatchesUrl, ThemisRoute } from '../edumono-route';
import { NavbarCategory } from '../category.model';
import { PermissionsService } from 'src/app/shared/permissions/permissions.service';
import { PERMISSIONS } from 'edu-core';

@Component({
  selector: 'edu-navbar',
  templateUrl: './edu-navbar.component.html',
  styleUrls: ['./edu-navbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class EduNavbarComponent implements OnInit, OnDestroy {
  sideNavOpen: boolean = false;
  currentRoute: ThemisRoute;
  navbarCollapsed: boolean = false;
  @ViewChild('fauxPicker')
  private readonly fauxPicker: MatDatepicker<null>;

  private destroy$ = new Subject<void>();

  public ngAfterViewInit(): void {
    if (this.fauxPicker !== undefined) {
      this.fauxPicker.open();
      this.changeDetectorRef.detectChanges();
      this.fauxPicker.close();
      this.changeDetectorRef.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Live enrollment count — null while loading, used to disable the class switcher */
  liveEnrollmentCount: number | null = null;

  constructor(
    private _snackBar: MatSnackBar,
    private _router: Router,
    private _loginService: LoginService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private _hackathonService: HackathonService,
    private _classPickerService: ClassPickerService,
    private _classesService: ClassesService,
    public selectedStudentService: SelectedStudentService,
    private _whatsappService: WhatsappService,
    private _organizationService: OrganizationService,
    private _permissions: PermissionsService,
  ) {
    this._router.events.pipe(takeUntil(this.destroy$)).subscribe((s) => {
      if (!this._router.url.includes('code')) {
        this.colorBloc.darkModeStream.next(false);
      }
      this.currentRoute = this._resolveCurrentRoute();
      this.sideNavOpen = false;
    });
  }

  get isAdminUser(): boolean {
  return this._loginService.userInfo?.type === 'admin';
}

  private _resolveCurrentRoute(): ThemisRoute | undefined {
    const url = this._router.url;
    return this.routes
      .filter((r) => routeMatchesUrl(url, r))
      .sort((a, b) => b.route.length - a.route.length)[0];
  }

  isRouteActive(route: ThemisRoute): boolean {
    return routeMatchesUrl(this._router.url, route);
  }

  toggleNavbarCollapse() {
    this.navbarCollapsed = !this.navbarCollapsed;
  }

  toggleSideNav(state?: boolean) {
    if (state == undefined) {
      this.sideNavOpen = !this.sideNavOpen;
      return;
    }
    this.sideNavOpen = state;
  }
  isAdmin: boolean = false;
  link: string = null;
  routes: ThemisRoute[] = [];
  viewableRoutes: ThemisRoute[] = [];

  logoImage = environment.assets.logo;
  orgImage = '';
  isDark: boolean = false;
  isHacker: boolean = false;
  colorBloc: CompilerColorBloc = CompilerColorBloc.CreateBloc();

  categories: NavbarCategory[] = [
    {
      id: 'teach',
      displayName: 'Content',
      expanded: true,
      default: 'expanded',
    },
    {
      id: 'progress',
      displayName: 'Progress & Assesment',
      expanded: true,
      default: 'expanded',
    },
    {
      id: 'plan',
      displayName: 'Planning',
      expanded: false,
      default: 'expanded',
    },
    {
      id: 'people',
      displayName: 'People',
      expanded: false,
      default: 'collapsed',
    },
    {
      id: 'comm',
      displayName: 'Communicate',
      expanded: false,
      default: 'collapsed',
    },
    {
      id: 'tools',
      displayName: 'Tools',
      expanded: false,
      default: 'collapsed',
    },
  ];
  noCategories: ThemisRoute[] = [];
  routesByCategoryCache: Map<string, ThemisRoute[]> = new Map();

  ngOnInit(): void {
    this.isAdmin = this._loginService.userInfo.type === 'team';
    this._createRoutes();
    if (this.isTeamMember) {
      this._organizationService.loadOrganizations();
      this._organizationService.selectedOrganization$
        .pipe(takeUntil(this.destroy$))
        .subscribe((org) => {
          this.orgImage = org?.logoUrl ?? '';
          this.changeDetectorRef.detectChanges();
        });
    }
    if (!this.isTeamMember && !this.isAdminUser) {
      this.selectedStudentService.cleared$
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this._classPickerService.open(true);
        });
      this._classesService
        .getClasses(this._loginService.userInfo.userName)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (result) => {
            const list = Array.isArray(result) ? result : [result];
            this.liveEnrollmentCount = list.length;

            if (
              this.selectedStudentService.hasSelection &&
              !list.some(
                (e) =>
                  e.studentId ===
                  this.selectedStudentService.studentInfo.studentId,
              )
            ) {
              this.selectedStudentService.clear();
            }
          },
          error: () => {
            this.liveEnrollmentCount = null;
          },
        });
    }
    this._buildRoutesByCategoryCache();
    this._loadStoredCategoryStates();
    this._loadWhatsappRouteIfAvailable();
    if (environment.org == 'CSTeam') {
      if (!this.isAdmin && this._loginService.userInfo?.type === 'student') {
        this._hackathonService
          .getAllCompetitions()
          .then((competitions) => {
            if (competitions.length > 0) {
              this.noCategories.push(
                new ThemisRoute(
                  'Hackathon',
                  false,
                  'assets/navbar/hackathon.svg',
                  '/portal/hackathon',
                  undefined,
                  false,
                ),
              );
            }

            this.changeDetectorRef.detectChanges();
          })
          .catch((error) => {
            console.error('❌ Failed to fetch competitions:', error);
            this.currentRoute = this.routes.find((route) =>
              this._router.url.includes(route.name.toLowerCase()),
            );
            this.viewableRoutes = this.routes.filter(
              (route) => route.name !== 'Trophies',
            );
            this.changeDetectorRef.detectChanges();
          });
      }
    }
    this.colorBloc.darkModeStream
      .pipe(takeUntil(this.destroy$))
      .subscribe((s) => {
        this.isDark = s;
      });
    this.colorBloc.hackerModeStream
      .pipe(takeUntil(this.destroy$))
      .subscribe((s) => {
        this.isHacker = s;
      });
    this.currentRoute = this._resolveCurrentRoute();
    this.viewableRoutes = this.routes.filter(
      (route) => route.name != 'Trophies',
    );
    this.noCategories = this.routes.filter(
      (route) => !route.category && !route.hidden,
    );
    this.nonEmptyCategories = this.getNonEmptyCategories();
  }

  goToLink() {
    if (this.link) {
      window.open(this.link, '_coursework');
    } else {
      this._snackBar.open(
        'Coursework is still loading try again in a second.',
        'Dismiss',
        {
          duration: 3 * 3600,
          horizontalPosition: 'end',
          verticalPosition: 'top',
        },
      );
    }
  }

  goToTrophies() {
    this._router.navigate(['portal', 'trophies']);
  }

  checkPage() {
    return this._router.url.includes('submissions/student/');
  }

  isFullHeightPage() {
    return this.checkPage() || this._router.url.includes('/portal/whatsapp');
  }

  get environment() {
    return environment;
  }

  get isTeamMember(): boolean {
    return this._loginService.userInfo?.type === 'team';
  }

  toggleCategory(category: NavbarCategory) {
    category.expanded = !category.expanded;
    localStorage.setItem(
      `nav-category-${category.id}`,
      category.expanded.toString(),
    );
  }

  private _loadStoredCategoryStates(): void {
    for (let category of this.categories) {
      const storedState = localStorage.getItem(`nav-category-${category.id}`);
      if (storedState !== null) {
        category.expanded = storedState === 'true';
      }
    }
  }

  private _buildRoutesByCategoryCache(): void {
    this.routesByCategoryCache.clear();
    for (let category of this.categories) {
      const filteredRoutes = this.routes.filter(
        (route) => route.category === category.id && !route.hidden,
      );
      this.routesByCategoryCache.set(category.id, filteredRoutes);
    }
  }

  getRoutesByCategory(categoryId: string): ThemisRoute[] {
    return this.routesByCategoryCache.get(categoryId) || [];
  }

  private _loadWhatsappRouteIfAvailable(): void {
    if (!this.isAdmin) {
      return;
    }

    this._whatsappService
      .isInboxAvailable()
      .pipe(takeUntil(this.destroy$))
      .subscribe((available) => {
        if (!available) {
          return;
        }

        const whatsappRoute = new ThemisRoute(
          'WhatsApp',
          true,
          'assets/navbar/whatsapp.svg',
          '/portal/whatsapp',
          'comm',
        );
        const insertBefore = this.routes.findIndex(
          (route) => route.name === 'Submissions',
        );
        if (insertBefore >= 0) {
          this.routes.splice(insertBefore, 0, whatsappRoute);
        } else {
          this.routes.push(whatsappRoute);
        }

        this._buildRoutesByCategoryCache();
        this.currentRoute = this._resolveCurrentRoute();
        this.viewableRoutes = this.routes.filter(
          (route) => route.name != 'Trophies',
        );
        this.noCategories = this.routes.filter(
          (route) => !route.category && !route.hidden,
        );
        this.nonEmptyCategories = this.getNonEmptyCategories();
        this.changeDetectorRef.detectChanges();
      });
  }

  private _createRoutes() {
    if (!this.isAdmin) {
      this.routes = [
        new ThemisRoute('Home', false, 'assets/navbar/home.svg'),
        new ThemisRoute('Submissions', false, 'assets/navbar/submissions.svg'),
        new ThemisRoute('Announcements', false, 'assets/navbar/comment.svg'),
        new ThemisRoute('Videos', false, 'assets/navbar/videos.svg'),
        new ThemisRoute('Materials', false, 'assets/navbar/folder-open.svg'),
        new ThemisRoute(
          'Trophies',
          false,
          'assets/navbar/trophy.svg',
          undefined,
          undefined,
          true,
        ),
      ];
    }
    if (this.isAdmin) {
      this.routes = [
        new ThemisRoute(
          'Dashboard',
          true,
          'assets/navbar/layout-fluid.svg',
          '/portal/dashboard',
        ),
        new ThemisRoute(
          'Videos',
          false,
          'assets/navbar/videos.svg',
          '/portal/videos',
          'teach',
          false,
          PERMISSIONS.educationResources.read,
        ),
        new ThemisRoute(
          'Materials',
          false,
          'assets/navbar/folder-open.svg',
          '/portal/materials',
          'teach',
          false,
          PERMISSIONS.educationResources.read,
        ),
        new ThemisRoute(
          'Announcements',
          false,
          'assets/navbar/comment.svg',
          '/portal/announcements/admin',
          'comm',
          false,
          PERMISSIONS.announcements.read,
        ),
        new ThemisRoute(
          'Students',
          true,
          'assets/navbar/users-alt.svg',
          '/portal/students',
          'people',
          false,
          PERMISSIONS.students.read,
        ),
        new ThemisRoute(
          'Registrants',
          true,
          'assets/navbar/pending.svg',
          '/portal/registrants',
          'people',
          false,
          PERMISSIONS.registrants.manage,
        ),
        new ThemisRoute(
          'Calls',
          true,
          'assets/navbar/phone-office.svg',
          '/portal/calls',
          'comm',
          false,
          PERMISSIONS.calls.read,
        ),
        new ThemisRoute(
          'Submissions',
          true,
          'assets/navbar/submissions.svg',
          '/portal/submissions/admin',
          'progress',
          false,
          PERMISSIONS.submissions.read,
        ),
        new ThemisRoute(
          'Live Sessions',
          true,
          'assets/navbar/link-horizontal.svg',
          '/portal/meetings',
          'plan',
          false,
          PERMISSIONS.meetings.read,
        ),
        new ThemisRoute(
          'Lectures',
          true,
          'assets/navbar/calendar-lines.svg',
          '/portal/lectures',
          'plan',
          false,
          PERMISSIONS.lectures.read,
        ),
        new ThemisRoute(
          'Reports',
          true,
          'assets/navbar/stats.svg',
          '/portal/reports',
          'progress',
          false,
          PERMISSIONS.reports.read,
        ),
        new ThemisRoute(
          'Hackathon',
          true,
          'assets/navbar/hackathon.svg',
          '/portal/hackathon/admin',
          'tools',
          false,
          PERMISSIONS.competitions.manage,
        ),
        new ThemisRoute(
          'AI',
          true,
          'assets/icons/ai-assistant.svg',
          '/portal/ai/jobs',
          'tools',
        ),
        new ThemisRoute(
          'Settings',
          true,
          'assets/navbar/settings.svg',
          '/portal/settings',
          'tools',
          false,
          PERMISSIONS.organizationSettings.manage,
        ),
      ];
    }
    if (this.isAdminUser) {
    this.routes = [
      new ThemisRoute(
        'Dashboard',
        true,
        'assets/navbar/layout-fluid.svg',
        '/portal/admin',
      ),
      new ThemisRoute(
        'Subjects',
        true,
        'assets/dashboard/subject.svg',
        '/portal/admin/subjects',
      ),
    ];
  }

    const studentInCsTeam =
      !this.isAdmin &&
      this.selectedStudentService.hasSelection &&
      this.selectedStudentService.studentInfo.organizationId ===
        environment.organizationId;
    const teamMemberInCsTeam =
      this.isTeamMember &&
      this._organizationService.currentOrganizationId ===
        environment.organizationId;
    const studentInIcteamSubject =
      !this.isAdmin &&
      this.selectedStudentService.hasSelection &&
      this.selectedStudentService.studentInfo.organizationId ===
        environment.icteamOrganizationId &&
      this.selectedStudentService.studentInfo.qualificationLevel ===
        environment.icteamSubject;
    const teamMemberInIcteam =
      this.isTeamMember &&
      this._organizationService.currentOrganizationId ===
        environment.icteamOrganizationId;
    if (
      studentInCsTeam ||
      teamMemberInCsTeam ||
      studentInIcteamSubject ||
      teamMemberInIcteam
    ) {
      this.routes.push(
        new ThemisRoute('Pseudocode', false, 'assets/navbar/browser.svg'),
      );
    }

    // One list for every team member, filtered by permission at the end of this
    // method. The old head / supervisor / quality branches pushed overlapping subsets
    // of exactly these routes, so the slugs reproduce that split without naming roles --
    // and a granted or denied override now moves the navigation with it.
    if (this.isAdmin) {
      this.routes.push(
        new ThemisRoute(
          'Groups',
          true,
          'assets/navbar/session-management-icon.svg',
          '/portal/session-management',
          'people',
          false,
          PERMISSIONS.sessions.manage,
        ),
        new ThemisRoute(
          'Payments',
          true,
          'assets/navbar/money-bill-wave.svg',
          '/portal/payments',
          'tools',
          false,
          PERMISSIONS.payments.manage,
        ),
        new ThemisRoute(
          'Topics',
          true,
          'assets/navbar/topics.svg',
          '/portal/topics',
          'plan',
          false,
          PERMISSIONS.topics.manage,
        ),
        new ThemisRoute(
          'Team',
          true,
          'assets/navbar/building.svg',
          '/portal/team',
          'people',
          false,
          PERMISSIONS.teamMembers.read,
        ),
        new ThemisRoute(
          'Milestones',
          true,
          'assets/navbar/flag.svg',
          '/portal/milestones',
          'progress',
          false,
          PERMISSIONS.milestones.read,
        ),
        new ThemisRoute(
          'Timings',
          true,
          'assets/icons/time-twenty-four.svg',
          '/portal/timings',
          'plan',
          false,
          PERMISSIONS.timings.manage,
        ),
        new ThemisRoute(
          'Support System',
          true,
          'assets/navbar/laptop-code.svg',
          '/portal/support-system',
          'progress',
          false,
          PERMISSIONS.supportSessions.read,
        ),
        new ThemisRoute(
          'Incidents',
          true,
          'assets/navbar/incident.svg',
          '/portal/incidents',
          'tools',
          false,
          PERMISSIONS.incidents.read,
        ),
        new ThemisRoute(
          'Team Excuses',
          true,
          'assets/navbar/profiles.svg',
          '/portal/team-excuses/all',
          'people',
          false,
          PERMISSIONS.teamExcuse.manage,
        ),
        new ThemisRoute(
          'Access Control',
          true,
          'assets/navbar/users-alt.svg',
          '/portal/access-control',
          'tools',
          false,
          PERMISSIONS.permissions.read,
        ),
      );
      // Leaderboard is an ICTeam-only screen, so the org check stays alongside the slug.
      if (
        this._organizationService.currentOrganizationId ===
        environment.icteamOrganizationId
      ) {
        this.routes.push(
          new ThemisRoute(
            'Leaderboard',
            true,
            'assets/navbar/ranking-star.svg',
            '/portal/leaderboard',
            'progress',
            false,
            PERMISSIONS.points.read,
          ),
        );
      }
      this.routes.push(
        new ThemisRoute(
          'Session Plans',
          true,
          'assets/icons/bars-progress.svg',
          '/portal/plans',
          'plan',
        ),
      );
    }

    if (environment.org == 'Papyrus' && this.isAdmin) {
      this.routes = [
        new ThemisRoute(
          'Dashboard',
          false,
          'assets/navbar/layout-fluid.svg',
          '/portal/dashboard',
        ),
        new ThemisRoute(
          'Submissions',
          false,
          'assets/navbar/submissions.svg',
          '/portal/submissions/admin',
        ),
        new ThemisRoute(
          'AI',
          true,
          'assets/icons/ai-assistant.svg',
          '/portal/ai/jobs',
        ),
      ];
    }

    // Runs last so it also covers the org-specific lists reassigned above. Routes
    // without a slug (Dashboard, AI, Pseudocode, the student and platform-admin
    // lists) are always kept.
    this.routes = this.routes.filter(
      (route) => !route.permission || this._permissions.has(route.permission),
    );
  }

  async openClassPicker(): Promise<void> {
    if (this.isTeamMember) {
      return;
    }
    // The StudentClassSwitchOverlay handles the reload after selection —
    // no router navigation needed here.
    await this._classPickerService.open();
  }

  getNonEmptyCategories(): NavbarCategory[] {
    return this.categories.filter(
      (category) => this.getRoutesByCategory(category.id).length > 0,
    );
  }

  nonEmptyCategories: NavbarCategory[];
}
